import * as S from './styles';
import { colors } from '@/constants';
import { ClientProps, DiscountProps, SalesCycleProps } from '@/types';
import React, { useMemo, useCallback } from 'react';
import { formatCode, formatCurrency, getOnStorage } from '@/shared';
import { useCart, useGlobals, useTables, useUserContext } from '@/hooks';
import { Accordion, ToolTip, ShortProgressBar, SeloCampanha } from '@/components';
import { calcularDiasAtras, formatarCEP, formatarData, formatarTelefone } from '@/shared/commonFunctions';
import {
  RiMapPin2Line,
  RiExchangeDollarLine,
  RiShoppingCartLine,
  RiCalendarEventLine,
  RiDirectionLine,
  RiCalendarScheduleLine,
  RiFlag2Line,
} from 'react-icons/ri';

const types = {
  1: 'default',
  2: 'success',
  3: 'warning',
  4: 'danger',
};

const Icons = {
  'warning': <RiFlag2Line color={colors.gray.gray50} fontSize={18} />,
  'default': <RiCalendarEventLine color={colors.gray.gray50} fontSize={18} />,
  'success': <RiCalendarEventLine color={colors.gray.gray50} fontSize={18} />,
  'danger': <RiCalendarScheduleLine color={colors.gray.gray50} fontSize={18} />,
}

const { userAgent } = navigator;
const tabletRegex = /iPad|tablet|playbook|silk|Macintosh|Windows|Linux|X11/i;
const mobileRegex = /Android|webOS|iPhone|iPod|BlackBerry|IEMobile|Opera Mini/i;
const isMobile = mobileRegex.test(userAgent) && !tabletRegex.test(userAgent);

const screenWidth = window.innerWidth;
const maxWidth = isMobile || screenWidth < 728 ? `${screenWidth - 30}px` : '80%';

export interface Props {
  index: number;
  isChecked?: boolean;
  showProgress?: boolean;
  client: ClientProps;
  setClient: (value: ClientProps | undefined) => void;
  isExpanded: boolean;
  toggleExpansion: () => void;

  // ⬇️ Novos callbacks (modal controlado pelo pai)
  onOpenBoleto: (client: ClientProps) => void;
  onOpenCreditLimit: (client: ClientProps) => void;
}

const ClientCard = ({
  showProgress,
  isChecked,
  client,
  setClient,
  isExpanded,
  toggleExpansion,
  onOpenBoleto,
  onOpenCreditLimit,
}: Props) => {
  const { tables } = useTables();
  const { cartDetails } = useCart();
  const { customer } = useUserContext();
  const { setCurrentModal } = useGlobals();

  const discounts = getOnStorage('discounts') as {
    [key: number]: DiscountProps;
  };

  const dateList = useMemo(() => {
    const raw = client.DT_ULTIMA_COMPRA;
    if (!raw) return [];
    const arr = Array.isArray(raw) ? [...raw] : (typeof raw === 'object' && raw !== null ? [raw] : []);
    if (!arr.length) return [];
    const toStr = (v: any) => String(v ?? '').replace(/\//g, '');
    return arr.sort((a: any, b: any) =>
      toStr(a?.dta_ult_compra) < toStr(b?.dta_ult_compra) ? 1 : -1
    );
  }, [client.DT_ULTIMA_COMPRA]);
  
  const hasDiscount = useMemo(() => !!discounts?.[client.COD_CLIENTE], [client.COD_CLIENTE, discounts]);

  const mountedCartsCount = useMemo(() => {
    const clientId = String(client.COD_CLIENTE);
    let scoped = 0;
    let legacy = 0;

    for (const [key, detail] of Object.entries(cartDetails || {})) {
      const owner = String(key).split(':')[0];
      if (owner !== clientId) continue;
      const hasItems = detail?.items?.some((item) => Number(item?.quantity) > 0);
      if (!hasItems) continue;
      if (String(key).includes(':')) scoped += 1;
      else legacy += 1;
    }

    return scoped || legacy;
  }, [cartDetails, client.COD_CLIENTE]);

  const { since, until, currentStatus, currentWeek, cycles } = useMemo(() => {
    let cycles: SalesCycleProps[] = [];
    const deValues: number[] = [];
    const ateValues: number[] = [];
    let current: SalesCycleProps | undefined;

    (client?.CYCLE ?? []).forEach((cycle) => {
      if (cycles.find((cy) => cy.SEMANA_CICLO_ID === cycle.SEMANA_CICLO_ID)) return;

      if (cycle.DE != null) deValues.push(cycle.DE);
      if (cycle.ATE != null) ateValues.push(cycle.DE);
      if (cycle.SEMANA_CICLO_ID_VIGENTE === cycle.SEMANA_CICLO_ID) current = cycle;
      cycles.push(cycle);
    });
    
    const since = deValues.length ? formatarData(Math.min(...deValues) || '', { year: '2-digit' }) : '';
    const until = ateValues.length ? formatarData(Math.max(...ateValues) || '', { year: '2-digit' }) : '';
    
    const currentWeek = current?.SEMANA_CICLO_ID_VIGENTE ?? 0;
    const currentStatus = current?.STATUS_POSITIVACAO ?? 1;
    return { since, until, currentStatus, currentWeek, cycles };
  }, [client.CYCLE]);

  const dtUltimaCompra = useMemo(() => {
    if (!dateList.length) return 0;
    const first = dateList[0]?.dta_ult_compra;
    const initial = first != null ? String(first) : '';
    if (!initial) return 0;
    const toStr = (v: any) => String(v ?? '').replace(/\//g, '');
    const date = [...dateList].reduce((acc: string, dt: any) => {
      const d = dt?.dta_ult_compra;
      if (d == null) return acc;
      const isMine = dt.nro_representante === customer?.codPessoa;
      const isMoreRecent = toStr(d) > toStr(acc);
      if (isMoreRecent && isMine) return String(d);
      return acc;
    }, initial);
    return date || first || 0;
  }, [customer?.codPessoa, dateList]);

  const datesMsg = useMemo(() => {
    if (!dateList?.length) return '';
    const List = dateList.map((date: any) => (
      <S.Row
        key={`${date.nro_segmento}-${date.dta_ult_compra}-${date.nro_representante}`}
        $marginTop={3}
        $marginBottom={3}
        $jcenter
      >
        <S.LabelLastPurchase key={date.nro_segmento}>
          <S.IndicatorMySales $isMine={date.nro_representante === customer?.codPessoa} />
          Segmento:&nbsp;
          <span>{date.nro_segmento}</span>&nbsp;&nbsp;&nbsp; Data da última Compra:&nbsp;
          <span>{formatarData(date.dta_ult_compra)}</span>
        </S.LabelLastPurchase>
      </S.Row>
    ));
    return <S.Column key={dtUltimaCompra}>{List}</S.Column>;
  }, [dtUltimaCompra, dateList, customer?.codPessoa]);

  const isDisabled = client.ST_COMPRA === 'I';
  const isDanger = client.daysWithoutPurchase >= 60;
  const isWarning = client.daysWithoutPurchase >= 30 && !isDanger;
  const hasAlert = isDanger || isWarning;
  const currentType = types[currentStatus] as S.CardColor;
  const pedidos = tables.current.eventos?.[client.COD_CLIENTE]?.boletos;

  const handleOpenBoletoModal = useCallback(
    (e?: React.MouseEvent) => {
      e?.stopPropagation();
      if (pedidos?.length) {
        setCurrentModal(`modal-boleto-${client.COD_CLIENTE}`);
        onOpenBoleto(client);
      }
    },
    [client, onOpenBoleto, pedidos?.length, setCurrentModal]
  );

  const handleOpenCreditLimitModal = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      setCurrentModal(`modal-credit-limit-${client.COD_CLIENTE}`);
      onOpenCreditLimit(client);
    },
    [client, onOpenCreditLimit, setCurrentModal]
  );

  const handleSelectClick = useCallback(() => {
    if (!isDisabled) setClient(isChecked ? undefined : client);
  }, [isDisabled, isChecked, client, setClient]);

  const handleArrowClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      toggleExpansion();
    },
    [toggleExpansion]
  );

  const boxStyle = useMemo(
    () => ({ cursor: isDisabled ? 'not-allowed' : 'pointer', width: '-webkit-fill-available' as const }),
    [isDisabled]
  );

  const cardFullWidthStyle = useMemo(() => ({ width: '100%' }), []);
  const arrowPointerStyle = useMemo(() => ({ cursor: 'pointer' }), []);

  return (
    <S.CheckedBox
      $ischecked={isChecked}
      onClick={handleSelectClick}
      style={boxStyle}
    >
      <S.Card $themecolor={currentType} style={cardFullWidthStyle}>
        <S.ContentInfos $space={!isExpanded}>
          <S.InfosContent>
            <S.Row $between>
              <S.Column>
                <SeloCampanha mode="cliente" codCliente={client.COD_CLIENTE} />
              </S.Column>
              <S.Column>
                <S.CompanieName data-test={`card-company-title-${client.COD_CLIENTE}`}>
                  <S.NumberCompanie $themecolor={currentType} $isActive={isChecked}>
                    {client.COD_CLIENTE}
                  </S.NumberCompanie>
                  <S.FantasyName $isdisabled={isDisabled}>{client.NM_CLIENTE}</S.FantasyName>
                </S.CompanieName>
                <S.CNPJLabel $isdisabled={isDisabled}>{formatCode(client.NR_DOCUMENTO)}</S.CNPJLabel>
              </S.Column>
              <S.ColumnRight>
                <S.Row>
                  {hasDiscount && <RiExchangeDollarLine color={colors.alert.green300} fontSize={24} />}
                  {mountedCartsCount > 0 && (
                    <S.CartIconWrap>
                      <RiShoppingCartLine color={colors.gray.gray50} fontSize={24} />
                      <S.BarNotification>{mountedCartsCount}</S.BarNotification>
                    </S.CartIconWrap>
                  )}
                  {isDisabled ? (
                    <S.ContentProhibited>
                      <S.ProhibitedIcon size={16} />
                    </S.ContentProhibited>
                  ) : (
                    <S.CheckContent>
                      <S.CheboxContent $isdisabled={isDisabled}>
                        {isChecked ? <S.CheckboxCircle size={24} /> : <S.CheckboxBlankCircle size={24} />}
                      </S.CheboxContent>
                    </S.CheckContent>
                  )}
                </S.Row>
                <S.Row $marginTop={4}>
                  {!client.notifications && (
                    <S.ContentBar className={!pedidos?.length ? 'disabled' : ''} onClick={handleOpenBoletoModal}>
                      <S.BarIcon size={24} />
                      {pedidos?.length && <S.BarNotification>{pedidos?.length}</S.BarNotification>}
                    </S.ContentBar>
                  )}
                  <ToolTip text="Em breve" displayMode="hover">
                    <RiDirectionLine className="disabled" fontSize={24} color={colors.gray.gray50} />
                  </ToolTip>
                </S.Row>
              </S.ColumnRight>
            </S.Row>

            {!!dtUltimaCompra && (
              <ToolTip maxWidth={maxWidth} text={datesMsg} displayMode="hover">
                <S.LastPurchaseContent $isdisabled={isDisabled} $themecolor={currentType}>
                  {Icons[currentType] || null}
                  <S.LastPurchase>
                    Última compra em {formatarData(dtUltimaCompra)} - {calcularDiasAtras(dtUltimaCompra)} dias
                  </S.LastPurchase>
                </S.LastPurchaseContent>
              </ToolTip>
            )}

            <S.AddresContent $isdisabled={isDisabled}>
              <S.MapContent $themecolor={currentType}>
                <RiMapPin2Line fontSize={18} />
              </S.MapContent>
              <S.Address>
                {`${client.ENDERECO}`}, {`${client.NUMERO}`} - {`${client.BAIRRO}`}, {`${client.CIDADE}`} - {`${client.ESTADO}`}
                <S.PRow $between>
                  <S.CEP>{formatarCEP(`${client.CEP}`)}</S.CEP>
                  <S.Phone>{formatarTelefone(client.NR_TELEFONE)}</S.Phone>
                </S.PRow>
              </S.Address>
            </S.AddresContent>

            <S.CycleContent $isdisabled={isDisabled}>
              <S.Cycle>
                <S.Label $marginBottom={12} $weight={500}>
                  Ciclo
                </S.Label>
                <S.SubLabel>
                  {since} a {until}
                </S.SubLabel>
                {!showProgress && (
                  <S.Row $marginTop={4} $between>
                    <S.ProgressContent>
                      {cycles.map((cycle) => {
                        return (
                          <ShortProgressBar
                            key={cycle.SEMANA_CICLO_ID}
                            isCurrent={cycle.SEMANA_CICLO_ID_VIGENTE === currentWeek}
                            state={cycle.POSITIVOU ? 'checked' : 'empty'}
                          />
                        );
                      })}
                    </S.ProgressContent>
                  </S.Row>
                )}
              </S.Cycle>
              <S.Separator />
              <S.Positivacao>
                <ToolTip text="Em breve" displayMode="hover">
                  <S.CardTitle className="disabled">Positivação de clientes</S.CardTitle>
                  <S.Row className="disabled" $between>
                    <S.SubLabel>Atingido</S.SubLabel>
                    <S.SubLabel>Pendente</S.SubLabel>
                  </S.Row>
                  <S.Row className="disabled" $between>
                    <S.Label>R$ ----</S.Label>
                    <S.Label>R$ ----</S.Label>
                  </S.Row>
                </ToolTip>
              </S.Positivacao>
            </S.CycleContent>
          </S.InfosContent>
        </S.ContentInfos>

        <S.ContentAlert $themecolor={currentType} $showBorder={hasAlert || isExpanded}>
          {hasAlert && (
            <S.AlertMessage type={isDanger ? 'danger' : 'warning'}>
              {isWarning && <S.FlagIcon size={15} />}
              {isDanger && <S.CalendarIcon size={15} />}
              Não compra há mais de
              <S.AlertMessageBold>{client.daysWithoutPurchase} dias</S.AlertMessageBold>
            </S.AlertMessage>
          )}
          <Accordion
            noRadius
            noBodyPadding
            accordionTitle=""
            showHeader={false}
            bgColor={colors.transparent}
            accordionExpanded={isExpanded}
            setAccordionExpanded={toggleExpansion}
            id={`company-${client.COD_CLIENTE}`}
          >
            <S.AccordionContent>
              <S.AccordionInfos $themecolor={currentType}>
                <S.AccordionDataInfo>
                  <S.AccordionLabel>Limite de crédito:</S.AccordionLabel>
                  <S.AccordionLimit>{formatCurrency({ value: client.LIMITE_CREDITO })}</S.AccordionLimit>
                </S.AccordionDataInfo>
                <S.AccordionDataInfo>
                  <S.AccordionLabel>Saldo disponível:</S.AccordionLabel>
                  <S.AccordionLimit>{formatCurrency({ value: client?.SALDO_DISPONIVEL || 0 })}</S.AccordionLimit>
                </S.AccordionDataInfo>
              </S.AccordionInfos>
              <S.RequestLimit onClick={handleOpenCreditLimitModal}>
                <S.IconPlus size={18} />
                Solicitar mais limite de crédito e/ou desbloqueio de cliente
              </S.RequestLimit>
            </S.AccordionContent>
          </Accordion>

          <S.ArrowContent
            $isOpened={isExpanded}
            onClick={handleArrowClick}
            style={arrowPointerStyle}
          >
            <S.OpenDetails>{!isExpanded ? 'Mais' : 'Menos'} detalhes</S.OpenDetails>
            <S.ArrowIcon size={15} $isOpened={isExpanded} />
          </S.ArrowContent>
        </S.ContentAlert>
      </S.Card>
    </S.CheckedBox>
  );
};

export default React.memo(ClientCard);
